import { useQuery } from '@apollo/client/react';
import { GET_USER_SETTINGS_AUTH } from '@scio3/graphql-client';
import { ROLE_TYPES } from '@scio3/shared-types';
import type { UserSettings } from '@scio3/shared-types';
import { useAuthStore } from '@/stores/auth-store';

interface GetUserSettingsAuthResult {
  getUserSettingsAuth: UserSettings | null;
}

/**
 * Determines whether the onboarding form should be shown.
 * Shows for account owners who haven't completed or skipped the onboarding form.
 * Skips for white-label accounts and embedded contexts.
 *
 * No loading state — the SignupPage prefetches all required data (me, company,
 * user settings) into the stores and Apollo cache before navigating, so by the
 * time AppShell mounts after signup the hook resolves synchronously.
 * For login, the hook simply returns false until data is available.
 */

/**
 * Only show the onboarding form for accounts created after this date.
 * Existing accounts before feature launch should never see the questionnaire.
 * Update this date to match the production deployment date of this feature.
 */
const ONBOARDING_LAUNCH_DATE = new Date('2026-03-16T00:00:00Z');

/** Set VITE_FORCE_ONBOARDING=true in sandbox to bypass the date gate for testing. */
const FORCE_ONBOARDING = import.meta.env.VITE_FORCE_ONBOARDING === 'true';

export function useShouldShowOnboarding(): boolean {
  const me = useAuthStore((s) => s.me);
  const company = useAuthStore((s) => s.company);
  const isEmbedded = useAuthStore((s) => s.isEmbedded);
  const token = useAuthStore((s) => s.token);
  const pendingOnboarding = useAuthStore((s) => s.pendingOnboarding);

  // Query uses auth token (not teamId), so skip only when no token.
  // network-only: must fetch fresh data on every login — cache-first can return
  // stale null from a clearStore() race, causing the form to flash unexpectedly.
  const { data, loading } = useQuery<GetUserSettingsAuthResult>(GET_USER_SETTINGS_AUTH, {
    skip: !token,
    fetchPolicy: 'network-only',
  });

  // SignupPage verified onboarding should show and set this flag before navigating.
  // Return true immediately so AppShell renders OnboardingForm on the first frame,
  // with zero gap between the signup page and the questionnaire.
  if (pendingOnboarding) {
    return true;
  }

  // Need me, company, and settings data to decide — until then, don't show.
  // Also wait for the query to finish loading to avoid flashing the form
  // when data is briefly null between clearStore() and network response.
  if (!me || !company || !data || loading) {
    return false;
  }

  // Existing accounts created before the feature launch (bypass with VITE_FORCE_ONBOARDING)
  if (!FORCE_ONBOARDING && new Date(company.createdAt) < ONBOARDING_LAUNCH_DATE) {
    return false;
  }

  // Quick exit for non-candidates
  if (isEmbedded || me.rootRole !== ROLE_TYPES.OWNER || me.brandingData?.isWhiteLabel === true) {
    return false;
  }

  const userSettings = data.getUserSettingsAuth;

  // No settings record = existing user who never triggered settings creation.
  // Don't show the onboarding form — it's only for brand-new signups.
  if (!userSettings) {
    return false;
  }

  return !userSettings.onboardFormSubmitted && !userSettings.onboardFormSkip;
}
